import React, { useMemo, useRef, useState } from 'react';
import { vibrar } from '../../utils/hapticos';
import { GastoPlan, ModoReparto, Plan } from '../../types';
import { useAMedias } from '../../context/AMediasContext';
import { useAuth } from '../../context/AuthContext';
import { usePerfil } from '../../context/PerfilContext';
import { usePlus } from '../../context/PlusContext';
import { LIMITES_USO } from '../../config/planes';
import { calcularReparto, formatoEuros, aCentimos, aEuros } from '../../utils/amediasUtils';
import { comprimirImagen } from '../../utils/imagenUtils';
import { Camera, Trash2, Eye } from 'lucide-react';
import { VisorTicket } from './VisorTicket';
import {
  Campo, claseInput, aInput, deInput, leerImporte, MensajeError, ModalBase, BotonesModal, chip,
} from './ui';

export const CATEGORIAS_GASTO = ['Comida', 'Alojamiento', 'Transporte', 'Ocio', 'Compras', 'Otros'];

interface Props {
  plan: Plan;
  gasto?: GastoPlan;
  /** Categorías ya usadas en el plan, para sugerirlas. */
  categoriasUsadas: string[];
  /** Gastos del plan que ya llevan foto, para el tope del plan gratis. */
  ticketsEnPlan: number;
  onClose: () => void;
}

const MODOS: { v: ModoReparto; label: string; ayuda: string }[] = [
  { v: 'iguales', label: 'A partes iguales', ayuda: 'Marca entre quién se reparte.' },
  { v: 'partes', label: 'Por partes', ayuda: 'Cuántas partes paga cada uno: 2 es el doble que 1.' },
  { v: 'exacto', label: 'Importes exactos', ayuda: 'Lo que le toca a cada uno; tiene que sumar el total.' },
];

export const ModalGasto: React.FC<Props> = ({ plan, gasto, categoriasUsadas, ticketsEnPlan, onClose }) => {
  const { crearGasto, actualizarGasto } = useAMedias();
  const { user } = useAuth();
  const { esPlus } = usePerfil();
  const { ofrecerPlus } = usePlus();

  // Los desactivados no salen, salvo que ya estuvieran en este gasto
  const participantes = useMemo(() => plan.participantes.filter(p =>
    p.activo || (gasto && (p.id in gasto.pagadoPor || p.id in gasto.importes))), [plan, gasto]);

  const yo = plan.participantes.find(p => p.uid === user?.uid);
  const pagadorInicial = gasto ? Object.keys(gasto.pagadoPor)[0] : (yo?.id ?? participantes[0]?.id ?? '');

  const [concepto, setConcepto] = useState(gasto?.concepto ?? '');
  const [importe, setImporte] = useState(gasto ? String(gasto.importe).replace('.', ',') : '');
  const [fecha, setFecha] = useState(aInput(gasto?.fecha ?? new Date()));
  const [categoria, setCategoria] = useState(gasto?.categoria ?? '');
  const [pagador, setPagador] = useState(pagadorInicial);
  const [modo, setModo] = useState<ModoReparto>(gasto?.reparto.modo ?? 'iguales');

  // Cada modo guarda lo suyo, para que cambiar de modo y volver no lo pierda
  const [incluidos, setIncluidos] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(participantes.map(p => [p.id,
      gasto ? (gasto.importes[p.id] ?? 0) > 0 : true])));
  const [pesos, setPesos] = useState<Record<string, string>>(() =>
    Object.fromEntries(participantes.map(p => [p.id,
      gasto?.reparto.modo === 'partes' ? String(gasto.reparto.partes[p.id] ?? 0) : '1'])));
  const [exactos, setExactos] = useState<Record<string, string>>(() =>
    Object.fromEntries(participantes.map(p => [p.id,
      gasto?.reparto.modo === 'exacto' && gasto.reparto.partes[p.id]
        ? String(gasto.reparto.partes[p.id]).replace('.', ',') : ''])));

  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState(false);

  // Foto del ticket: undefined = sin cambios, null = quitarla, texto = la nueva
  const [ticket, setTicket] = useState<string | null | undefined>(undefined);
  const [procesando, setProcesando] = useState(false);
  const [viendo, setViendo] = useState(false);
  const inputFoto = useRef<HTMLInputElement>(null);
  const tieneTicket = typeof ticket === 'string' || (ticket === undefined && !!gasto?.conTicket);

  // Plan gratis: LIMITES_USO.ticketsPorPlan fotos por plan. Cambiar la de un
  // gasto que ya tenía no suma; ponérsela a uno que no tenía, sí.
  const pedirFoto = () => {
    const suma = !gasto?.conTicket;
    if (!esPlus && suma && ticketsEnPlan >= LIMITES_USO.ticketsPorPlan) { ofrecerPlus('tickets'); return; }
    inputFoto.current?.click();
  };

  const elegirFoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setProcesando(true);
    setError('');
    try { setTicket(await comprimirImagen(file)); } catch (err: any) {
      setError(err?.message || 'No se pudo usar esa foto.');
    } finally { setProcesando(false); }
  };

  const total = leerImporte(importe);

  const partes: Record<string, number> = useMemo(() => Object.fromEntries(participantes.map(p => [p.id,
    modo === 'iguales' ? (incluidos[p.id] ? 1 : 0)
      : modo === 'partes' ? leerImporte(pesos[p.id] ?? '')
      : leerImporte(exactos[p.id] ?? '')])), [participantes, modo, incluidos, pesos, exactos]);

  // El pagador primero: si sobra un céntimo, que se lo quede quien ya ha pagado
  const orden = useMemo(() => [pagador, ...participantes.map(p => p.id).filter(id => id !== pagador)],
    [pagador, participantes]);
  const reparto = useMemo(() => calcularReparto(total, modo, partes, orden), [total, modo, partes, orden]);

  const pendienteExacto = modo === 'exacto'
    ? aEuros(aCentimos(total) - Object.values(partes).reduce((s, v) => s + aCentimos(v), 0))
    : 0;

  const categorias = useMemo(() => {
    const vistas = new Map<string, string>();
    [...CATEGORIAS_GASTO, ...categoriasUsadas].forEach(c => {
      if (!vistas.has(c.toLowerCase())) vistas.set(c.toLowerCase(), c);
    });
    return Array.from(vistas.values());
  }, [categoriasUsadas]);

  const enviar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!concepto.trim()) { setError('Indica qué es el gasto.'); return; }
    if (total <= 0) { setError('Indica el importe.'); return; }
    if (!pagador) { setError('Indica quién pagó.'); return; }
    if (!fecha) { setError('Indica la fecha.'); return; }
    if (!reparto.ok) { setError(reparto.error); return; }

    setGuardando(true);
    setError('');
    // Solo se guardan las partes de quien entra en el reparto
    const partesGuardadas = Object.fromEntries(Object.entries(partes).filter(([, v]) => v > 0));
    const datos = {
      concepto: concepto.trim(),
      importe: aEuros(aCentimos(total)),
      fecha: deInput(fecha)!,
      categoria: categoria.trim() || undefined,
      pagadoPor: { [pagador]: aEuros(aCentimos(total)) },
      reparto: { modo, partes: partesGuardadas },
      importes: reparto.importes,
    };
    try {
      if (gasto) await actualizarGasto(plan.id, gasto, datos, ticket);
      else await crearGasto(plan.id, datos, ticket ?? undefined);
      vibrar.exito();
      onClose();
    } catch (err) {
      console.error(err);
      vibrar.error();
      setError('No se pudo guardar. Inténtalo de nuevo.');
      setGuardando(false);
    }
  };

  const todosMarcados = participantes.every(p => incluidos[p.id]);

  return (
    <ModalBase titulo={gasto ? 'Editar gasto' : 'Nuevo gasto'} onClose={onClose}>
      <form onSubmit={enviar} className="p-5 space-y-4">
        <MensajeError texto={error} />

        <Campo label="Concepto *">
          <input type="text" value={concepto} onChange={e => setConcepto(e.target.value)}
            placeholder="Cena, gasolina, apartamento…" className={claseInput} autoFocus />
        </Campo>

        <div className="grid gap-3 grid-cols-2">
          <Campo label="Importe (€) *">
            <input type="text" inputMode="decimal" value={importe} onChange={e => setImporte(e.target.value)}
              placeholder="0,00" className={claseInput} />
          </Campo>
          <Campo label="Fecha *">
            <input type="date" value={fecha} onChange={e => setFecha(e.target.value)} className={claseInput} />
          </Campo>
        </div>

        <div className="grid gap-3 grid-cols-2">
          <Campo label="Pagó *">
            <select value={pagador} onChange={e => setPagador(e.target.value)} className={claseInput}>
              {participantes.map(p => (
                <option key={p.id} value={p.id}>{p.id === yo?.id ? `${p.nombre} (tú)` : p.nombre}</option>
              ))}
            </select>
          </Campo>
          <Campo label="Categoría">
            <input type="text" value={categoria} onChange={e => setCategoria(e.target.value)}
              list="categorias-gasto-plan" placeholder="Opcional" className={claseInput} />
            <datalist id="categorias-gasto-plan">
              {categorias.map(c => <option key={c} value={c} />)}
            </datalist>
          </Campo>
        </div>

        <Campo label="Cómo se reparte" ayuda={MODOS.find(m => m.v === modo)?.ayuda}>
          <div className="flex flex-wrap gap-1">
            {MODOS.map(m => (
              <button key={m.v} type="button" onClick={() => setModo(m.v)} className={chip(modo === m.v)}>
                {m.label}
              </button>
            ))}
          </div>
        </Campo>

        <div className="rounded-xl border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700">
          {modo === 'iguales' && (
            <button type="button"
              onClick={() => setIncluidos(Object.fromEntries(participantes.map(p => [p.id, !todosMarcados])))}
              className="w-full text-left px-3 py-2 text-xs font-semibold text-emerald-700 dark:text-emerald-400 hover:underline">
              {todosMarcados ? 'Quitar a todos' : 'Marcar a todos'}
            </button>
          )}
          {participantes.map(p => {
            const leToca = reparto.ok ? reparto.importes[p.id] ?? 0 : undefined;
            return (
              <div key={p.id} className="flex items-center gap-3 px-3 py-2">
                {modo === 'iguales' ? (
                  <label className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer">
                    <input type="checkbox" checked={!!incluidos[p.id]}
                      onChange={e => setIncluidos(prev => ({ ...prev, [p.id]: e.target.checked }))}
                      className="w-4 h-4 accent-emerald-600" />
                    <span className="truncate text-sm text-gray-800 dark:text-gray-200">{p.nombre}</span>
                  </label>
                ) : (
                  <>
                    <span className="flex-1 min-w-0 truncate text-sm text-gray-800 dark:text-gray-200">{p.nombre}</span>
                    <input type="text" inputMode="decimal"
                      value={modo === 'partes' ? pesos[p.id] ?? '' : exactos[p.id] ?? ''}
                      onChange={e => (modo === 'partes' ? setPesos : setExactos)(prev => ({ ...prev, [p.id]: e.target.value }))}
                      placeholder={modo === 'partes' ? '0' : '0,00'}
                      aria-label={`${modo === 'partes' ? 'Partes' : 'Importe'} de ${p.nombre}`}
                      className={`${claseInput} !w-24 text-right`} />
                  </>
                )}
                {modo !== 'exacto' && (
                  <span className="w-20 text-right text-sm tabular-nums text-gray-500 dark:text-gray-400">
                    {leToca !== undefined && leToca > 0 ? formatoEuros(leToca) : '—'}
                  </span>
                )}
              </div>
            );
          })}
          {modo === 'exacto' && total > 0 && (
            <p className={`px-3 py-2 text-xs font-semibold ${
              pendienteExacto === 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-amber-700 dark:text-amber-400'
            }`}>
              {pendienteExacto === 0 ? 'Cuadra con el total.'
                : pendienteExacto > 0 ? `Quedan ${formatoEuros(pendienteExacto)} por repartir.`
                : `Te pasas ${formatoEuros(-pendienteExacto)} del total.`}
            </p>
          )}
        </div>

        <Campo label="Foto del ticket" ayuda="Opcional. Se guarda reducida; la ven todos los del plan.">
          <input ref={inputFoto} type="file" accept="image/*" capture="environment" onChange={elegirFoto} className="hidden" />
          {tieneTicket ? (
            <div className="flex items-center gap-3">
              {typeof ticket === 'string' ? (
                <img src={ticket} alt="Ticket" className="w-14 h-14 object-cover rounded-lg border border-gray-200 dark:border-gray-700" />
              ) : (
                <span className="text-sm text-gray-700 dark:text-gray-300">Ticket adjunto</span>
              )}
              <button type="button" onClick={() => setViendo(true)}
                className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 dark:text-emerald-400 hover:underline">
                <Eye className="w-3.5 h-3.5" /> Ver
              </button>
              <button type="button" onClick={pedirFoto}
                className="inline-flex items-center gap-1 text-xs font-semibold text-gray-600 dark:text-gray-400 hover:underline">
                <Camera className="w-3.5 h-3.5" /> Cambiar
              </button>
              <button type="button" onClick={() => setTicket(null)}
                className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 dark:text-red-400 hover:underline">
                <Trash2 className="w-3.5 h-3.5" /> Quitar
              </button>
            </div>
          ) : (
            <button type="button" disabled={procesando} onClick={pedirFoto}
              className="inline-flex items-center justify-center gap-2 py-2.5 rounded-lg border border-dashed
                         border-gray-300 dark:border-gray-600 text-sm text-gray-600 dark:text-gray-400
                         hover:border-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-400 transition disabled:opacity-60">
              <Camera className="w-4 h-4" /> {procesando ? 'Preparando foto…' : 'Hacer o subir foto'}
            </button>
          )}
        </Campo>

        <BotonesModal guardando={guardando || procesando} texto={gasto ? 'Guardar cambios' : 'Añadir gasto'} onClose={onClose} />
        {viendo && (
          <VisorTicket planId={plan.id} gastoId={gasto?.id} imagen={typeof ticket === 'string' ? ticket : undefined}
            titulo={concepto || 'Ticket'} onClose={() => setViendo(false)} />
        )}
      </form>
    </ModalBase>
  );
};
